import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Send, Paperclip, AlertCircle, CheckCircle } from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const TicketsManagement = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await api.get('/tickets');
      setTickets(response.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل التذاكر' : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async (ticketId) => {
    try {
      const response = await api.get(`/tickets/${ticketId}/replies`);
      setReplies(response.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل الردود' : 'Failed to load replies');
    }
  };

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    fetchReplies(ticket.id);
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedTicket) return;

    try {
      await api.post('/tickets/replies', {
        ticket_id: selectedTicket.id,
        message: replyMessage
      });
      toast.success(i18n.language === 'ar' ? 'تم إرسال الرد' : 'Reply sent');
      setReplyMessage('');
      fetchReplies(selectedTicket.id);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إرسال الرد' : 'Failed to send reply');
    }
  };

  const stats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي التذاكر' : 'Total Tickets',
      value: tickets.length,
      icon: MessageSquare,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: i18n.language === 'ar' ? 'مفتوحة' : 'Open',
      value: tickets.filter(t => t.status === 'open').length,
      icon: AlertCircle,
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      title: i18n.language === 'ar' ? 'محلولة' : 'Resolved',
      value: tickets.filter(t => t.status === 'resolved').length,
      icon: CheckCircle,
      color: 'from-green-500 to-green-600'
    },
  ];

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">{i18n.language === 'ar' ? 'نظام التذاكر' : 'Ticketing System'}</h1>
            <p className="text-text-secondary text-lg">{i18n.language === 'ar' ? 'إدارة طلبات الدعم الفني' : 'Manage support requests'}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-text-secondary mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle>{i18n.language === 'ar' ? 'التذاكر' : 'Tickets'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => handleSelectTicket(ticket)}
                    className={`p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedTicket?.id === ticket.id ? 'bg-primary/5 border-primary' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="font-semibold text-sm line-clamp-1">{ticket.subject}</p>
                      <Badge variant={ticket.status === 'open' ? 'default' : 'secondary'} className="text-xs">
                        {ticket.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-text-secondary">{ticket.ticket_number}</p>
                    <p className="text-xs text-text-secondary mt-1">{format(new Date(ticket.created_at), 'dd/MM/yyyy')}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{i18n.language === 'ar' ? 'التفاصيل' : 'Details'}</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedTicket ? (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-2">{selectedTicket.subject}</h3>
                      <p className="text-text-secondary">{selectedTicket.description}</p>
                      <div className="flex items-center gap-3 mt-4">
                        <Badge>{selectedTicket.priority}</Badge>
                        <span className="text-sm text-text-secondary">{format(new Date(selectedTicket.created_at), 'dd/MM/yyyy HH:mm')}</span>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h4 className="font-semibold mb-4">{i18n.language === 'ar' ? 'الردود' : 'Replies'}</h4>
                      <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                        {replies.map((reply) => (
                          <div key={reply.id} className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm mb-2">{reply.message}</p>
                            <p className="text-xs text-text-secondary">{format(new Date(reply.created_at), 'dd/MM/yyyy HH:mm')}</p>
                          </div>
                        ))}
                      </div>

                      <form onSubmit={handleSendReply} className="space-y-3">
                        <Textarea
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          placeholder={i18n.language === 'ar' ? 'اكتب ردك...' : 'Write your reply...'}
                          rows={4}
                        />
                        <Button type="submit" className="btn-primary gap-2">
                          <Send className="w-4 h-4" />
                          {i18n.language === 'ar' ? 'إرسال' : 'Send'}
                        </Button>
                      </form>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <MessageSquare className="w-16 h-16 text-text-secondary mx-auto mb-4" />
                    <p className="text-text-secondary">{i18n.language === 'ar' ? 'اختر تذكرة لعرض التفاصيل' : 'Select a ticket to view details'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TicketsManagement;